import { Filter, Inbox, ListChecks, Search, Shield } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	type Alert,
	type AlertStatus,
	MOCK_ADMIN_STATS,
	MOCK_ALERTS,
	MOCK_USERS,
	type ScoreLevel,
} from '@/data/aml/mockData';
import { cn } from '@/lib/utils';
import { Routes } from '@/routes/routes';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MY_QUEUE_IDS = ['user_4', 'user_5', 'user_6'];

function scoreClass(level: ScoreLevel): string {
	if (level === 'very_high' || level === 'high')
		return 'bg-red-50 text-red-700 border-red-200';
	if (level === 'medium')
		return 'bg-orange-50 text-orange-700 border-orange-200';
	return 'bg-green-50 text-green-700 border-green-200';
}

function statusClass(status: AlertStatus): string {
	switch (status) {
		case 'open':
			return 'bg-blue-50 text-blue-700 border-blue-200';
		case 'closed':
			return 'bg-gray-100 text-gray-700 border-gray-200';
		case 'disposed':
			return 'bg-purple-50 text-purple-700 border-purple-200';
		case 'pending':
			return 'bg-yellow-50 text-yellow-700 border-yellow-200';
		case 'hibernated':
			return 'bg-cyan-50 text-cyan-700 border-cyan-200';
		default:
			return 'bg-gray-100 text-gray-600';
	}
}

function slaClass(sla: string): string {
	return sla === 'breached'
		? 'bg-red-50 text-red-700 border-red-200'
		: 'bg-green-50 text-green-700 border-green-200';
}

function moduleLabel(name: string): string {
	if (name === 'transaction_monitoring') return 'TM';
	return 'NS';
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

// ── Alert Table ────────────────────────────────────────────────────────────────

function AlertTable({
	alerts,
	loading = false,
}: {
	alerts: Alert[];
	loading?: boolean;
}) {
	const navigate = useNavigate();

	if (loading) {
		return (
			<div className="rounded-xl border border-[#E5E5E5] bg-white p-8 text-center text-sm text-[#737373]">
				Loading alerts...
			</div>
		);
	}

	if (alerts.length === 0) {
		return (
			<div className="rounded-xl border border-dashed border-[#D4D4D4] bg-white p-10 text-center text-sm text-[#737373]">
				No alerts found.
			</div>
		);
	}

	return (
		<div className="rounded-xl border border-[#E5E5E5] bg-white overflow-hidden">
			<Table>
				<TableHeader>
					<TableRow className="bg-[#FAFAFA]">
						<TableHead className="text-xs font-semibold text-[#737373]">
							Alert
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Entity
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Module
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Score
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Status
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							SLA
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Assigned To
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Created
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{alerts.map((alert) => (
						<TableRow
							key={alert.id}
							className="cursor-pointer hover:bg-[#FAFAFA] transition-colors"
							onClick={() =>
								navigate(Routes.AML_ALERT_DETAIL.replace(':id', alert.id))
							}
						>
							<TableCell>
								<span className="text-sm font-semibold text-[#0A0A0A]">
									{alert.code}
								</span>
							</TableCell>
							<TableCell className="max-w-[200px]">
								<div className="text-sm text-[#0A0A0A] truncate">
									{alert.entity_name}
								</div>
								<div className="text-xs text-[#737373]">
									{alert.customer_id}
								</div>
							</TableCell>
							<TableCell>
								<span className="inline-flex items-center rounded-md bg-[#F0F0F0] px-1.5 py-0.5 text-xs font-medium text-[#646464]">
									{moduleLabel(alert.module_name)}
								</span>
							</TableCell>
							<TableCell>
								<span
									className={cn(
										'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold',
										scoreClass(alert.score.level),
									)}
								>
									{alert.score.level.replace('_', ' ').toUpperCase()} ·{' '}
									{alert.score.value}%
								</span>
							</TableCell>
							<TableCell>
								<span
									className={cn(
										'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
										statusClass(alert.status),
									)}
								>
									{alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
								</span>
							</TableCell>
							<TableCell>
								<span
									className={cn(
										'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
										slaClass(alert.sla_status),
									)}
								>
									{alert.sla_status === 'breached' ? 'Breached' : 'On Track'}
								</span>
							</TableCell>
							<TableCell>
								<span className="text-sm text-[#525252]">
									{alert.assigned_to?.name ?? 'Unassigned'}
								</span>
							</TableCell>
							<TableCell>
								<span className="text-xs text-[#737373]">
									{formatDate(alert.created_at)}
								</span>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

// ── Filter bar + table combo ──────────────────────────────────────────────────

function AlertListContent({
	alerts,
	showHibernatedToggle = false,
}: {
	alerts: Alert[];
	showHibernatedToggle?: boolean;
}) {
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [scoreFilter, setScoreFilter] = useState('all');
	const [slaFilter, setSlaFilter] = useState('all');
	const [assigneeFilter, setAssigneeFilter] = useState('all');
	const [moduleFilter, setModuleFilter] = useState('all');
	const [showHibernated, setShowHibernated] = useState(false);
	const [page, setPage] = useState(1);
	const PAGE_SIZE = 15;

	const filtered = useMemo(() => {
		let result = alerts;
		if (!showHibernated) result = result.filter((a) => !a.is_hibernated);
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(a) =>
					a.entity_name.toLowerCase().includes(q) ||
					a.code.toLowerCase().includes(q) ||
					a.customer_id.toLowerCase().includes(q),
			);
		}
		if (statusFilter !== 'all')
			result = result.filter((a) => a.status === statusFilter);
		if (scoreFilter !== 'all')
			result = result.filter((a) => a.score.level === scoreFilter);
		if (slaFilter !== 'all')
			result = result.filter((a) => a.sla_status === slaFilter);
		if (moduleFilter !== 'all')
			result = result.filter((a) => a.module_name === moduleFilter);
		if (assigneeFilter === 'unassigned')
			result = result.filter((a) => !a.assigned_to);
		else if (assigneeFilter !== 'all')
			result = result.filter((a) => a.assigned_to?.id === assigneeFilter);
		return result;
	}, [
		alerts,
		search,
		statusFilter,
		scoreFilter,
		slaFilter,
		assigneeFilter,
		moduleFilter,
		showHibernated,
	]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const hasFilters =
		statusFilter !== 'all' ||
		scoreFilter !== 'all' ||
		slaFilter !== 'all' ||
		assigneeFilter !== 'all' ||
		moduleFilter !== 'all';

	return (
		<div className="space-y-4 mt-6">
			<Card className="border-border/60 shadow-sm">
				<div className="flex flex-wrap items-center gap-3 p-3 bg-muted/20">
					<span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mr-2">
						<Filter className="h-3.5 w-3.5" /> Filters
					</span>
					<Select
						value={moduleFilter}
						onValueChange={(v) => {
							setModuleFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[160px] h-8 text-xs bg-background">
							<SelectValue placeholder="All Modules" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Modules</SelectItem>
							<SelectItem value="name_screening">Name Screening</SelectItem>
							<SelectItem value="transaction_monitoring">
								Transaction Monitoring
							</SelectItem>
						</SelectContent>
					</Select>
					<Select
						value={statusFilter}
						onValueChange={(v) => {
							setStatusFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[150px] h-8 text-xs bg-background">
							<SelectValue placeholder="All Statuses" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Statuses</SelectItem>
							<SelectItem value="open">Open</SelectItem>
							<SelectItem value="closed">Closed</SelectItem>
							<SelectItem value="disposed">Disposed</SelectItem>
							<SelectItem value="pending">Pending</SelectItem>
						</SelectContent>
					</Select>
					<Select
						value={scoreFilter}
						onValueChange={(v) => {
							setScoreFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[130px] h-8 text-xs bg-background">
							<SelectValue placeholder="All Scores" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Scores</SelectItem>
							<SelectItem value="very_high">Very High</SelectItem>
							<SelectItem value="high">High</SelectItem>
							<SelectItem value="medium">Medium</SelectItem>
							<SelectItem value="low">Low</SelectItem>
						</SelectContent>
					</Select>
					<Select
						value={slaFilter}
						onValueChange={(v) => {
							setSlaFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[130px] h-8 text-xs bg-background">
							<SelectValue placeholder="All SLAs" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All SLAs</SelectItem>
							<SelectItem value="breached">Breached</SelectItem>
							<SelectItem value="on_track">On Track</SelectItem>
						</SelectContent>
					</Select>
					<Select
						value={assigneeFilter}
						onValueChange={(v) => {
							setAssigneeFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[160px] h-8 text-xs bg-background">
							<SelectValue placeholder="All Agents" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Agents</SelectItem>
							<SelectItem value="unassigned">Unassigned</SelectItem>
							{MOCK_USERS.map((u) => (
								<SelectItem key={u.id} value={u.id}>
									{u.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{showHibernatedToggle && (
						<div className="flex items-center gap-2 ml-2 pl-4 border-l">
							<Checkbox
								id="hibernated"
								checked={showHibernated}
								onCheckedChange={(c) => {
									setShowHibernated(!!c);
									setPage(1);
								}}
							/>
							<label
								htmlFor="hibernated"
								className="text-xs font-medium cursor-pointer"
							>
								Show Hibernated
							</label>
						</div>
					)}
					{hasFilters && (
						<Button
							variant="ghost"
							size="sm"
							className="h-8 text-xs text-muted-foreground ml-auto"
							onClick={() => {
								setStatusFilter('all');
								setScoreFilter('all');
								setSlaFilter('all');
								setAssigneeFilter('all');
								setModuleFilter('all');
								setPage(1);
							}}
						>
							Clear Filters
						</Button>
					)}
				</div>
			</Card>

			<div className="flex items-center justify-between gap-4">
				<div className="relative w-72">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(1);
						}}
						placeholder="Search by entity, ID or code..."
						className="pl-9 h-9 bg-background shadow-sm"
					/>
				</div>
				<span className="text-sm text-[#737373]">{filtered.length} alerts</span>
			</div>

			<AlertTable alerts={paginated} />

			{totalPages > 1 && (
				<div className="flex items-center justify-end gap-2 pt-2">
					<Button
						variant="outline"
						size="sm"
						disabled={page <= 1}
						onClick={() => setPage((p) => p - 1)}
					>
						Previous
					</Button>
					<span className="text-sm text-[#737373]">
						Page {page} of {totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						disabled={page >= totalPages}
						onClick={() => setPage((p) => p + 1)}
					>
						Next
					</Button>
				</div>
			)}
		</div>
	);
}

// ── Admin View ────────────────────────────────────────────────────────────────

function AlertAdminView() {
	const stats = MOCK_ADMIN_STATS;
	return (
		<div className="mt-6 space-y-4">
			<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
				{[
					{ label: 'Total Alerts', value: stats.total_alerts },
					{ label: 'Open Alerts', value: stats.open_alerts },
					{ label: 'SLA Breached', value: stats.breached_sla },
					{ label: 'High Priority', value: stats.high_priority_alerts },
				].map((s) => (
					<Card key={s.label} className="p-4">
						<div className="text-sm text-[#737373]">{s.label}</div>
						<div className="text-3xl font-bold text-[#0A0A0A] mt-1">
							{s.value}
						</div>
					</Card>
				))}
			</div>
			<AlertListContent alerts={MOCK_ALERTS} showHibernatedToggle />
		</div>
	);
}

// ── Page ──────────────────────────────────────────────────────────────────────

type Tab = 'my-queue' | 'all' | 'admin';

const myQueueAlerts = MOCK_ALERTS.filter(
	(a) => a.assigned_to && MY_QUEUE_IDS.includes(a.assigned_to.id),
);

export default function AlertListingPage() {
	const [activeTab, setActiveTab] = useState<Tab>('my-queue');

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />

			<div className="border-b border-border bg-white px-6 py-4">
				<div className="flex flex-col gap-1">
					<h1 className="text-xl font-semibold text-[#0A0A0A]">
						Name Screening Alerts
					</h1>
					<p className="text-sm text-[#737373]">
						Review and manage AML name screening alerts
					</p>
				</div>
			</div>

			<div className="p-6">
				<Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>
					<TabsList className="bg-muted">
						<TabsTrigger value="my-queue" className="gap-2">
							<Inbox className="h-4 w-4" /> My Queue
							{myQueueAlerts.filter((a) => a.status === 'open').length > 0 && (
								<Badge
									variant="default"
									className="ml-1 h-5 px-1.5 py-0 text-[10px]"
								>
									{myQueueAlerts.filter((a) => a.status === 'open').length}
								</Badge>
							)}
						</TabsTrigger>
						<TabsTrigger value="all" className="gap-2">
							<ListChecks className="h-4 w-4" /> All Alerts
							<Badge
								variant="secondary"
								className="ml-1 h-5 px-1.5 py-0 text-[10px] bg-muted-foreground/20"
							>
								{MOCK_ALERTS.length}
							</Badge>
						</TabsTrigger>
						<TabsTrigger value="admin" className="gap-2">
							<Shield className="h-4 w-4" /> Admin View
						</TabsTrigger>
					</TabsList>

					<TabsContent value="my-queue">
						<AlertListContent alerts={myQueueAlerts} />
					</TabsContent>
					<TabsContent value="all">
						<AlertListContent alerts={MOCK_ALERTS} showHibernatedToggle />
					</TabsContent>
					<TabsContent value="admin">
						<AlertAdminView />
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
